/**
 * CNC PM PWA — Status Classification Engine (statusCalculator.ts)
 * 
 * Authoritative Mathematical Maintenance Status Calculation
 * Derived from ORIGINAL_REQUEST.md (§ R3), PROJECT.md, and cnc_pm_checklist.xlsx
 */

import type { Machine, MachineStatus, FrequencyType, InspectionRecord } from '../../types';

export type PMStatus = MachineStatus; // 'NORMAL' | 'DUE_SOON' | 'OVERDUE'
export type PMFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface FrequencyThresholdConfig {
  periodMs: number;
  periodHours: number;
  periodDays: number;
  dueSoonWindowMs: number;
  dueSoonWindowHours: number;
  dueSoonWindowDays: number;
}

export const FREQUENCY_CONFIG: Record<PMFrequency, FrequencyThresholdConfig> = {
  daily: {
    periodMs: 24 * 3600 * 1000,
    periodHours: 24,
    periodDays: 1,
    dueSoonWindowMs: 4 * 3600 * 1000,
    dueSoonWindowHours: 4, // Due soon when elapsed >= 20h
    dueSoonWindowDays: 4 / 24,
  },
  weekly: {
    periodMs: 7 * 24 * 3600 * 1000,
    periodHours: 168,
    periodDays: 7,
    dueSoonWindowMs: 24 * 3600 * 1000,
    dueSoonWindowHours: 24, // Due soon when elapsed >= 6 days (144h)
    dueSoonWindowDays: 1,
  },
  monthly: {
    periodMs: 30 * 24 * 3600 * 1000,
    periodHours: 720,
    periodDays: 30,
    dueSoonWindowMs: 5 * 24 * 3600 * 1000,
    dueSoonWindowHours: 120, // Due soon when elapsed >= 25 days (600h)
    dueSoonWindowDays: 5,
  },
  yearly: {
    periodMs: 365 * 24 * 3600 * 1000,
    periodHours: 8760,
    periodDays: 365,
    dueSoonWindowMs: 30 * 24 * 3600 * 1000,
    dueSoonWindowHours: 720, // Due soon when elapsed >= 335 days (8040h)
    dueSoonWindowDays: 30,
  },
};

export const OPERATING_HOURS_TIERS = [500, 1000, 2000, 8000, 10000] as const;
export type OperatingHoursTier = (typeof OPERATING_HOURS_TIERS)[number];

export const TIER_DESCRIPTIONS: Record<number, string> = {
  500: 'Check slideway & ballscrew oil film',
  1000: 'Slideway lube & AP2 ballscrew greasing',
  2000: 'Hydraulic oil change (HM 46), Central lube (G 220), Table gearbox & wipers',
  8000: 'Spindle bearings regrease (Klüber NBU 15) & NC battery replacement',
  10000: 'Milling head secondary gearbox oil change',
};

export interface ServiceMilestoneAlert {
  tier: number;
  status: PMStatus;
  hoursElapsed: number;
  remainingHours: number;
  progressPercent: number;
  estimatedDueDate: string;
  description: string;
}

export interface FrequencyStatusDetail {
  status: PMStatus;
  elapsedMs: number;
  elapsedHours: number;
  elapsedDays: number;
  deadlineMs: number;
  remainingMs: number;
  remainingHours: number;
  reason?: string;
}

export interface MachineScheduleResult {
  overallStatus: PMStatus;
  frequencyStatuses: Record<PMFrequency, FrequencyStatusDetail>;
  hoursMilestones: ServiceMilestoneAlert[];
  reasons: string[];
  worstCaseReason: string;
  deltaHours: number;
  nextMajorMilestone?: ServiceMilestoneAlert;
}

export interface MachineScheduleInput {
  cumulativeHours: number;
  lastServiceHours?: number;
  lastInspectionDates?: Partial<Record<PMFrequency, string | null>> | Record<string, string | null>;
  lastInspectionDate?: string | null;
  lastHoursServiceSnapshot?: Record<number, number>;
  frequency?: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | PMFrequency;
  targetMilestone?: number;
  avgDailyOperatingHours?: number; // default 16
  now?: Date;
}

/**
 * Severity ranking for worst-case aggregation:
 * OVERDUE (3) > DUE_SOON (2) > NORMAL (1)
 */
export const STATUS_SEVERITY: Record<PMStatus, number> = {
  OVERDUE: 3,
  DUE_SOON: 2,
  NORMAL: 1,
};

/**
 * Normalizes frequency string to canonical PMFrequency ('daily' | 'weekly' | 'monthly' | 'yearly')
 */
export function normalizeFrequency(freq?: string): PMFrequency {
  if (!freq) return 'daily';
  const lower = freq.toLowerCase();
  if (lower.includes('day') || lower.includes('daily')) return 'daily';
  if (lower.includes('week')) return 'weekly';
  if (lower.includes('month')) return 'monthly';
  if (lower.includes('year')) return 'yearly';
  return 'daily';
}

/**
 * Primary mathematical calculation function conforming to project specification and test oracles.
 */
export function calculateMachineStatus(params: {
  cumulativeHours: number;
  lastServiceHours?: number;
  lastInspectionDate?: string | null;
  frequency?: string;
  targetMilestone?: number;
  now?: Date;
}): { status: PMStatus; reasons: string[]; deltaHours: number; hoursMilestones?: ServiceMilestoneAlert[] } {
  const {
    cumulativeHours,
    lastServiceHours = 0,
    lastInspectionDate,
    frequency = 'DAILY',
    targetMilestone,
    now = new Date(),
  } = params;

  let status: PMStatus = 'NORMAL';
  const reasons: string[] = [];

  // 1. Operating Hours Thresholds
  const deltaHours = Math.max(0, cumulativeHours - lastServiceHours);
  const milestonesToCheck = targetMilestone ? [targetMilestone] : [...OPERATING_HOURS_TIERS];

  for (const milestone of milestonesToCheck) {
    if (deltaHours >= milestone) {
      status = 'OVERDUE';
      reasons.push(`Exceeded ${milestone}h operating milestone (elapsed: ${deltaHours.toFixed(1)}h)`);
    } else if (deltaHours >= 0.9 * milestone && status !== 'OVERDUE') {
      status = 'DUE_SOON';
      reasons.push(`Approaching ${milestone}h operating milestone (${deltaHours.toFixed(1)}h / ${milestone}h)`);
    }
  }

  // 2. Calendar Frequency Thresholds
  if (lastInspectionDate) {
    const lastTime = new Date(lastInspectionDate).getTime();
    const currentTime = new Date(now).getTime();
    const elapsedMs = currentTime - lastTime;
    const elapsedHours = elapsedMs / (1000 * 60 * 60);
    const elapsedDays = elapsedHours / 24;

    const freqKey = normalizeFrequency(frequency);

    if (freqKey === 'daily') {
      if (elapsedHours >= 24) {
        status = 'OVERDUE';
        reasons.push(`Daily PM overdue by ${(elapsedHours - 24).toFixed(1)} hours`);
      } else if (elapsedHours >= 20 && status !== 'OVERDUE') {
        status = 'DUE_SOON';
        reasons.push(`Daily PM due soon (last inspected ${elapsedHours.toFixed(1)}h ago)`);
      }
    } else if (freqKey === 'weekly') {
      if (elapsedDays >= 7) {
        status = 'OVERDUE';
        reasons.push(`Weekly PM overdue by ${(elapsedDays - 7).toFixed(1)} days`);
      } else if (elapsedDays >= 6 && status !== 'OVERDUE') {
        status = 'DUE_SOON';
        reasons.push(`Weekly PM due soon (last inspected ${elapsedDays.toFixed(1)} days ago)`);
      }
    } else if (freqKey === 'monthly') {
      if (elapsedDays >= 30) {
        status = 'OVERDUE';
        reasons.push(`Monthly PM overdue by ${(elapsedDays - 30).toFixed(1)} days`);
      } else if (elapsedDays >= 25 && status !== 'OVERDUE') {
        status = 'DUE_SOON';
        reasons.push(`Monthly PM due soon (last inspected ${elapsedDays.toFixed(1)} days ago)`);
      }
    } else if (freqKey === 'yearly') {
      if (elapsedDays >= 365) {
        status = 'OVERDUE';
        reasons.push(`Yearly PM overdue by ${(elapsedDays - 365).toFixed(1)} days`);
      } else if (elapsedDays >= 335 && status !== 'OVERDUE') {
        status = 'DUE_SOON';
        reasons.push(`Yearly PM due soon (last inspected ${elapsedDays.toFixed(1)} days ago)`);
      }
    }
  } else {
    // If never inspected, it is OVERDUE
    status = 'OVERDUE';
    reasons.push('Machine has no recorded inspection');
  }

  return { status, reasons, deltaHours };
}

/**
 * Full Multi-Frequency & Multi-Milestone Schedule Calculator
 * Conforms to survey_explorer_integrations/report.md § 2.4
 */
export function calculateMachinePMStatus(
  input: MachineScheduleInput,
  now: Date = new Date()
): MachineScheduleResult {
  const nowMs = now.getTime();
  let overallStatus: PMStatus = 'NORMAL';
  const reasons: string[] = [];

  const frequencyStatuses: Record<PMFrequency, FrequencyStatusDetail> = {} as any;

  // Resolve lastInspectionDates dictionary
  const datesMap: Partial<Record<PMFrequency, string | null>> = {};
  if (input.lastInspectionDates) {
    for (const [key, val] of Object.entries(input.lastInspectionDates)) {
      datesMap[normalizeFrequency(key)] = val;
    }
  } else if (input.lastInspectionDate !== undefined) {
    const singleFreq = normalizeFrequency(input.frequency as string);
    datesMap[singleFreq] = input.lastInspectionDate;
  }

  // 1. Evaluate Periodic Inspection Windows for all 4 frequencies if datesMap provided
  const frequenciesToCheck: PMFrequency[] = input.lastInspectionDates
    ? ['daily', 'weekly', 'monthly', 'yearly']
    : [normalizeFrequency(input.frequency as string)];

  for (const freq of frequenciesToCheck) {
    const cfg = FREQUENCY_CONFIG[freq];
    const dateStr = datesMap[freq];

    if (!dateStr) {
      const reason = `No previous ${freq} inspection record found.`;
      frequencyStatuses[freq] = {
        status: 'OVERDUE',
        elapsedMs: Infinity,
        elapsedHours: Infinity,
        elapsedDays: Infinity,
        deadlineMs: 0,
        remainingMs: 0,
        remainingHours: 0,
        reason,
      };
      overallStatus = 'OVERDUE';
      reasons.push(reason);
      continue;
    }

    const lastTime = new Date(dateStr).getTime();
    const elapsedMs = nowMs - lastTime;
    const elapsedHours = elapsedMs / (1000 * 3600);
    const elapsedDays = elapsedHours / 24;
    const remainingMs = cfg.periodMs - elapsedMs;
    const remainingHours = remainingMs / (1000 * 3600);

    let freqStatus: PMStatus = 'NORMAL';
    let freqReason: string | undefined;

    if (elapsedMs >= cfg.periodMs) {
      freqStatus = 'OVERDUE';
      const overHours = Math.max(0, (elapsedMs - cfg.periodMs) / 3600000);
      freqReason = `${freq.toUpperCase()} inspection is overdue by ${Math.round(overHours)}h.`;
    } else if (remainingMs <= cfg.dueSoonWindowMs) {
      freqStatus = 'DUE_SOON';
      const remHours = Math.max(0, remainingMs / 3600000);
      freqReason = `${freq.toUpperCase()} inspection is due soon (within ${Math.round(remHours)}h).`;
    }

    frequencyStatuses[freq] = {
      status: freqStatus,
      elapsedMs,
      elapsedHours,
      elapsedDays,
      deadlineMs: lastTime + cfg.periodMs,
      remainingMs,
      remainingHours,
      reason: freqReason,
    };

    if (freqStatus === 'OVERDUE') {
      overallStatus = 'OVERDUE';
      if (freqReason) reasons.push(freqReason);
    } else if (freqStatus === 'DUE_SOON' && overallStatus !== 'OVERDUE') {
      overallStatus = 'DUE_SOON';
      if (freqReason) reasons.push(freqReason);
    }
  }

  // 2. Evaluate Operating Hours Milestones
  const avgHoursPerDay = input.avgDailyOperatingHours || 16;
  const hoursMilestones: ServiceMilestoneAlert[] = [];
  const cumulativeHours = input.cumulativeHours || 0;
  const defaultLastService = input.lastServiceHours || 0;

  for (const tier of OPERATING_HOURS_TIERS) {
    const lastServicedAt =
      input.lastHoursServiceSnapshot?.[tier] !== undefined
        ? input.lastHoursServiceSnapshot[tier]
        : defaultLastService;

    const hoursElapsed = Math.max(0, cumulativeHours - lastServicedAt);
    const remainingHours = Math.max(0, tier - hoursElapsed);
    const progressPercent = Math.min(100, Math.max(0, (hoursElapsed / tier) * 100));

    let tierStatus: PMStatus = 'NORMAL';
    let tierReason: string | undefined;

    if (hoursElapsed >= tier) {
      tierStatus = 'OVERDUE';
      tierReason = `${tier}h service milestone exceeded by ${(hoursElapsed - tier).toFixed(1)} hours.`;
    } else if (hoursElapsed >= 0.9 * tier || remainingHours <= tier * 0.1) {
      tierStatus = 'DUE_SOON';
      tierReason = `${tier}h service milestone due soon (${remainingHours.toFixed(1)} hours remaining).`;
    }

    const daysUntilDue = remainingHours / avgHoursPerDay;
    const estimatedDueDate = new Date(nowMs + daysUntilDue * 86400000).toISOString();

    const milestoneAlert: ServiceMilestoneAlert = {
      tier,
      status: tierStatus,
      hoursElapsed,
      remainingHours,
      progressPercent,
      estimatedDueDate,
      description: TIER_DESCRIPTIONS[tier] || `Service Milestone ${tier}h`,
    };

    hoursMilestones.push(milestoneAlert);

    if (tierStatus === 'OVERDUE') {
      overallStatus = 'OVERDUE';
      if (tierReason) reasons.push(tierReason);
    } else if (tierStatus === 'DUE_SOON' && overallStatus !== 'OVERDUE') {
      overallStatus = 'DUE_SOON';
      if (tierReason) reasons.push(tierReason);
    }
  }

  // Find next major milestone (first upcoming or overdue milestone)
  const nextMajorMilestone =
    hoursMilestones.find((m) => m.status === 'OVERDUE' || m.status === 'DUE_SOON') ||
    hoursMilestones[0];

  const worstCaseReason =
    reasons.length > 0
      ? reasons[0]
      : 'All PM inspections and running hours are up-to-date.';

  return {
    overallStatus,
    frequencyStatuses,
    hoursMilestones,
    reasons,
    worstCaseReason,
    deltaHours: Math.max(0, cumulativeHours - defaultLastService),
    nextMajorMilestone,
  };
}

/**
 * Convenience evaluator for Machine entity from local Dexie database
 */
export function evaluateMachine(
  machine: Machine,
  inspections?: InspectionRecord[],
  now: Date = new Date()
): MachineScheduleResult {
  // Build last inspection dates from inspection history if available
  const datesMap: Partial<Record<PMFrequency, string | null>> = {
    daily: null,
    weekly: null,
    monthly: null,
    yearly: null,
  };

  if (inspections && inspections.length > 0) {
    for (const insp of inspections) {
      const f = normalizeFrequency(insp.frequency);
      if (!datesMap[f] && insp.inspectionDate) {
        datesMap[f] = insp.inspectionDate;
      }
    }
  }

  // If no detailed records, fallback to machine.lastInspectionDate for daily
  if (!datesMap.daily && machine.lastInspectionDate) {
    datesMap.daily = machine.lastInspectionDate;
  }

  return calculateMachinePMStatus(
    {
      cumulativeHours: machine.cumulativeHours || 0,
      lastServiceHours: machine.lastInspectionHours || 0,
      lastInspectionDates: machine.lastInspectionDate ? datesMap : undefined,
      lastInspectionDate: machine.lastInspectionDate || null,
      frequency: 'daily',
    },
    now
  );
}
